import { getCurrentUser } from '@/lib/auth-utils'
import { fetchAccessibleCompanyIds } from '@/lib/company-access'
import { ROLE_ADMIN } from '@/lib/permissions'
import { neon } from '@/services/neon'

export interface DashboardStats {
  totalProperties: number
  occupancyRate: number
  monthlyRevenue: number
  upcomingBookings: number
}

export interface RecentBooking {
  id: string
  guest_name: string
  property_title: string
  check_in: string
  check_out: string
  status: string
}

export interface UpcomingCheckin {
  guest_name: string
  property_title: string
  check_in: string
  check_out: string
  type: 'checkin' | 'checkout'
}

function getCompanyId(): string | null {
  const user = getCurrentUser()
  if (!user?.id) return null
  if (user.role === ROLE_ADMIN) return null
  return user.company_id || null
}

function applyCompanyFilter<T extends Record<string, unknown>>(
  query: any,
  companyIds: string[],
): any {
  if (companyIds.length === 1) return query.eq('company_id', companyIds[0])
  if (companyIds.length > 1) return query.in('company_id', companyIds)
  return query
}

export async function fetchDashboardStats(): Promise<DashboardStats> {
  const user = getCurrentUser()
  if (!user?.id) throw new Error('Not authenticated')

  const companyIds = user.role === ROLE_ADMIN ? [] : await fetchAccessibleCompanyIds(user.id)
  if (user.role !== ROLE_ADMIN && companyIds.length === 0) {
    return { totalProperties: 0, occupancyRate: 0, monthlyRevenue: 0, upcomingBookings: 0 }
  }

  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
  const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1)
    .toISOString()
    .split('T')[0]

  let propQuery = neon
    .from('properties')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'active')
    .is('deleted_at', null)
  if (companyIds.length > 0) {
    propQuery = applyCompanyFilter(propQuery, companyIds)
  }

  let upcomingQuery = neon
    .from('bookings')
    .select('id', { count: 'exact', head: true })
    .gte('check_in', monthStart)
    .is('deleted_at', null)
    .in('status', ['confirmed', 'pending'])
  if (companyIds.length > 0) {
    upcomingQuery = applyCompanyFilter(upcomingQuery, companyIds)
  }

  let revenueQuery = neon
    .from('bookings')
    .select('check_in,check_out,property_id')
    .gte('check_in', monthStart)
    .lt('check_in', nextMonthStart)
    .is('deleted_at', null)
    .in('status', ['confirmed', 'completed'])
  if (companyIds.length > 0) {
    revenueQuery = applyCompanyFilter(revenueQuery, companyIds)
  }

  const [{ count: totalProps }, { count: upcoming }, bookingsRes] = await Promise.all([
    propQuery,
    upcomingQuery,
    revenueQuery,
  ])

  const monthlyBookings =
    (bookingsRes.data as Array<{ property_id: string; check_in: string; check_out: string }>) || []

  let totalRevenue = 0
  if (monthlyBookings.length > 0) {
    const propIds = [...new Set(monthlyBookings.map((b) => b.property_id))]
    const { data: props } = await neon
      .from('properties')
      .select('id,price')
      .in('id', propIds)
      .is('deleted_at', null)
    const priceMap: Record<string, number> = {}
    ;((props as Array<{ id: string; price: number }>) || []).forEach((p) => {
      priceMap[p.id] = Number(p.price) || 0
    })
    totalRevenue = monthlyBookings.reduce((sum, b) => {
      const nights = b.check_out
        ? Math.max(
            1,
            Math.ceil(
              (new Date(b.check_out).getTime() - new Date(b.check_in).getTime()) /
                (1000 * 60 * 60 * 24),
            ),
          )
        : 1
      return sum + (priceMap[b.property_id] || 0) * nights
    }, 0)
  }

  const totalPropsNum = totalProps ?? 0
  const upcomingNum = upcoming ?? 0
  const occupancyRate =
    totalPropsNum > 0
      ? Math.round((new Set(monthlyBookings.map((b) => b.property_id)).size / totalPropsNum) * 100)
      : 0

  return {
    totalProperties: totalPropsNum,
    occupancyRate,
    monthlyRevenue: totalRevenue,
    upcomingBookings: upcomingNum,
  }
}

export async function fetchRecentBookings(limit = 5): Promise<RecentBooking[]> {
  const user = getCurrentUser()
  if (!user?.id) throw new Error('Not authenticated')

  const companyIds = user.role === ROLE_ADMIN ? [] : await fetchAccessibleCompanyIds(user.id)
  if (user.role !== ROLE_ADMIN && companyIds.length === 0) return []

  let query = neon
    .from('bookings')
    .select('id,guest_name,check_in,check_out,status,property_id')
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (companyIds.length > 0) {
    query = applyCompanyFilter(query, companyIds)
  }

  const { data, error } = await query
  if (error) throw new Error(error.message)

  const rows =
    (data as Array<{
      id: string
      guest_name: string
      check_in: string
      check_out: string
      status: string
      property_id: string
    }>) || []

  if (rows.length === 0) return []

  const propIds = [...new Set(rows.map((r) => r.property_id))]
  const { data: props } = await neon
    .from('properties')
    .select('id,title')
    .in('id', propIds)
    .is('deleted_at', null)
  const titleMap: Record<string, string> = {}
  ;((props as Array<{ id: string; title: string }>) || []).forEach((p) => {
    titleMap[p.id] = p.title
  })

  return rows.map((r) => ({
    id: r.id,
    guest_name: r.guest_name,
    property_title: titleMap[r.property_id] || 'Unknown',
    check_in: r.check_in,
    check_out: r.check_out,
    status: r.status,
  }))
}

export async function fetchUpcomingCheckins(limit = 5): Promise<UpcomingCheckin[]> {
  const user = getCurrentUser()
  if (!user?.id) throw new Error('Not authenticated')

  const companyIds = user.role === ROLE_ADMIN ? [] : await fetchAccessibleCompanyIds(user.id)
  if (user.role !== ROLE_ADMIN && companyIds.length === 0) return []

  const today = new Date().toISOString().split('T')[0]

  let query = neon
    .from('bookings')
    .select('id,guest_name,check_in,check_out,status,property_id')
    .gte('check_out', today)
    .is('deleted_at', null)
    .in('status', ['confirmed', 'pending'])
    .order('check_in', { ascending: true })
    .limit(limit * 2)
  if (companyIds.length > 0) {
    query = applyCompanyFilter(query, companyIds)
  }

  const { data, error } = await query
  if (error) throw new Error(error.message)

  const rows =
    (data as Array<{
      id: string
      guest_name: string
      check_in: string
      check_out: string
      status: string
      property_id: string
    }>) || []

  if (rows.length === 0) return []

  const propIds = [...new Set(rows.map((r) => r.property_id))]
  const { data: props } = await neon
    .from('properties')
    .select('id,title')
    .in('id', propIds)
    .is('deleted_at', null)
  const titleMap: Record<string, string> = {}
  ;((props as Array<{ id: string; title: string }>) || []).forEach((p) => {
    titleMap[p.id] = p.title
  })

  const events: UpcomingCheckin[] = []
  for (const r of rows) {
    const isCheckinSoon = r.check_in <= today || daysUntil(r.check_in, today) <= 2
    const isCheckoutSoon = daysUntil(r.check_out, today) <= 2

    if (events.length >= limit) break

    if (isCheckinSoon) {
      events.push({
        guest_name: r.guest_name,
        property_title: titleMap[r.property_id] || 'Unknown',
        check_in: r.check_in,
        check_out: r.check_out,
        type: 'checkin',
      })
    } else if (isCheckoutSoon) {
      events.push({
        guest_name: r.guest_name,
        property_title: titleMap[r.property_id] || 'Unknown',
        check_in: r.check_in,
        check_out: r.check_out,
        type: 'checkout',
      })
    }
  }

  if (events.length < limit) {
    for (const r of rows) {
      if (events.length >= limit) break
      const exists = events.some(
        (e) =>
          e.guest_name === r.guest_name &&
          e.property_title === (titleMap[r.property_id] || 'Unknown'),
      )
      if (!exists) {
        const dIn = daysUntil(r.check_in, today)
        const dOut = daysUntil(r.check_out, today)
        let type: 'checkin' | 'checkout' = 'checkin'
        if (dIn >= 0 && dIn <= 2) type = 'checkin'
        else if (dOut >= 0 && dOut <= 2) type = 'checkout'
        else type = r.check_in <= today ? 'checkout' : 'checkin'
        events.push({
          guest_name: r.guest_name,
          property_title: titleMap[r.property_id] || 'Unknown',
          check_in: r.check_in,
          check_out: r.check_out,
          type,
        })
      }
    }
  }

  return events.slice(0, limit)
}

export interface MonthlyRevenue {
  month: string
  label: string
  revenue: number
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export async function fetchMonthlyRevenue(monthsBack = 12): Promise<MonthlyRevenue[]> {
  const user = getCurrentUser()
  if (!user?.id) throw new Error('Not authenticated')

  const companyIds = user.role === ROLE_ADMIN ? [] : await fetchAccessibleCompanyIds(user.id)
  if (user.role !== ROLE_ADMIN && companyIds.length === 0) {
    const result: MonthlyRevenue[] = []
    const now2 = new Date()
    for (let i = 0; i < monthsBack; i++) {
      const d = new Date(now2.getFullYear(), now2.getMonth() - i, 1)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      result.unshift({ month: key, label: MONTH_LABELS[d.getMonth()], revenue: 0 })
    }
    return result
  }

  const now = new Date()
  const startMonth = new Date(now.getFullYear(), now.getMonth() - monthsBack + 1, 1)
  const endMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)

  const startStr = startMonth.toISOString().split('T')[0]
  const endStr = endMonth.toISOString().split('T')[0]

  let query = neon
    .from('bookings')
    .select('check_in,check_out,property_id')
    .gte('check_in', startStr)
    .lt('check_in', endStr)
    .is('deleted_at', null)
    .in('status', ['confirmed', 'completed'])
  if (companyIds.length > 0) {
    query = applyCompanyFilter(query, companyIds)
  }

  const { data, error } = await query
  if (error) throw new Error(error.message)

  const rows = (data as Array<{ check_in: string; check_out: string; property_id: string }>) || []

  const result: MonthlyRevenue[] = []
  for (let i = 0; i < monthsBack; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    result.unshift({ month: key, label: MONTH_LABELS[d.getMonth()], revenue: 0 })
  }

  if (rows.length === 0) return result

  const propIds = [...new Set(rows.map((r) => r.property_id))]
  const { data: props } = await neon
    .from('properties')
    .select('id,price')
    .in('id', propIds)
    .is('deleted_at', null)
  const priceMap: Record<string, number> = {}
  ;((props as Array<{ id: string; price: number }>) || []).forEach((p) => {
    priceMap[p.id] = Number(p.price) || 0
  })

  for (const r of rows) {
    const monthKey = r.check_in.slice(0, 7)
    const entry = result.find((m) => m.month === monthKey)
    if (entry) {
      const nights = r.check_out
        ? Math.max(
            1,
            Math.ceil(
              (new Date(r.check_out).getTime() - new Date(r.check_in).getTime()) /
                (1000 * 60 * 60 * 24),
            ),
          )
        : 1
      entry.revenue += (priceMap[r.property_id] || 0) * nights
    }
  }

  return result
}

function daysUntil(dateStr: string, fromStr: string): number {
  const d1 = new Date(dateStr).getTime()
  const d2 = new Date(fromStr).getTime()
  return Math.ceil((d1 - d2) / (1000 * 60 * 60 * 24))
}
